import React, { useState } from "react";
import type { OfferInputs } from "../../types/ambassadors";
import NumberField from "./NumberField";
import { committedByYear } from "./calc";
import { count, pct, plural } from "./format";

// Internal only (never in Present mode): the company-wide revenue-share budget,
// year by year — how much of Request's platform revenue is already committed
// across ambassadors (every saved offer added up), what the open offer
// proposes, and what's left under the budget. Year 1 is each offer's own first
// year (offers have no start dates). The budget is a shared setting, changed in
// place with "Change".

interface Props {
  /** The saved offers that count as committed (in the calculator: all but the open one). */
  others: OfferInputs[];
  /** The open offer: its shares are the proposal. */
  current?: OfferInputs;
  /** % of platform revenue all ambassadors together may be promised in a year. */
  budgetPct: number;
  /** Saves a new shared budget; rejects if the server refused it. */
  onSaveBudget: (pct: number) => Promise<void>;
}

const share = (n: number | undefined) => (n ? pct(n) : "0%");
// What's left can drop below zero: a minus, and the cell turns red.
const left = (n: number) => (Math.abs(n) < 0.0005 ? "0%" : n < 0 ? `−${pct(-n)}` : pct(n));

export default function RevenueBudget({ others, current, budgetPct, onSaveBudget }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<number | null>(budgetPct);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const committed = committedByYear(others);
  const proposal = current ? committedByYear([current]) : null;
  const years = Math.max(committed.byYear.length, proposal ? proposal.byYear.length : 0);
  const remaining = Array.from({ length: years }, (_, k) => budgetPct - (committed.byYear[k] ?? 0) - (proposal?.byYear[k] ?? 0));
  const overYears = remaining.map((r, k) => (r < -0.0005 ? k + 1 : 0)).filter(Boolean);
  const proposalKnown = !!proposal && proposal.counted > 0;
  const validDraft = draft !== null && draft >= 0 && draft <= 100;

  const save = async () => {
    if (!validDraft) return;
    setSaving(true);
    setError("");
    try {
      await onSaveBudget(draft as number);
      setEditing(false);
    } catch {
      setError("Couldn’t save the budget. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  const notes = [
    `${plural(committed.counted, current ? "other saved offer" : "saved offer")} committed`,
    committed.skipped.length ? `not counted until complete: ${committed.skipped.join(", ")}` : "",
    proposal && !proposalKnown ? "this offer counts once it’s complete" : "",
    "Year 1 = each offer’s first year",
  ];

  return (
    <section className="adm-card amb-card">
      <div className="amb-card-head">
        <h3 className="adm-section-title">Revenue-share budget</h3>
        {editing ? (
          <span className="amb-budget-edit">
            <NumberField label="Revenue-share budget" suffix="%" required value={draft} onChange={setDraft} />
            <button type="button" className="adm-btn-green" disabled={!validDraft || saving} onClick={save}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button type="button" className="adm-btn-light" disabled={saving} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </span>
        ) : (
          <span className="amb-budget-line">
            Budget <b>{count(budgetPct)}%</b> of platform revenue a year
            <button type="button" className="adm-btn-text" onClick={() => { setDraft(budgetPct); setError(""); setEditing(true); }}>
              Change
            </button>
          </span>
        )}
      </div>
      <p className="amb-note amb-committed-sub">
        Company-wide: the share of Request platform revenue promised to ambassadors in each partnership year.
      </p>
      {error && <p className="error-text">{error}</p>}
      {years > 0 && (
        <div className="amb-scroll-x">
          <table className="amb-committed">
            <thead>
              <tr>
                <th />
                {Array.from({ length: years }, (_, k) => (
                  <th key={k}>Y{k + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Committed</th>
                {Array.from({ length: years }, (_, k) => (
                  <td key={k}>{share(committed.byYear[k])}</td>
                ))}
              </tr>
              {proposal && (
                <tr>
                  <th scope="row">This offer</th>
                  {Array.from({ length: years }, (_, k) => (
                    <td key={k}>{proposalKnown ? share(proposal.byYear[k]) : "—"}</td>
                  ))}
                </tr>
              )}
              <tr className="amb-budget-left">
                <th scope="row">Remaining</th>
                {remaining.map((r, k) => (
                  <td key={k} className={r < -0.0005 ? "amb-over" : undefined}>
                    {left(r)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {overYears.length > 0 && (
        <p className="amb-over-note">
          Over the {count(budgetPct)}% budget in {overYears.map((y) => `Year ${y}`).join(", ")}.
        </p>
      )}
      <p className="amb-note">{notes.filter(Boolean).join(" · ")}.</p>
    </section>
  );
}
